import React from "react";

import { Box, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useCardSize } from "components/Report-generator/context/CardSizeContext";
import type { CashFlowTimelineCardData } from "store/types";

interface Props {
  data?: CashFlowTimelineCardData[];
  loading?: boolean;
}

export const CashFlowTimelineCard: React.FC<Props> = ({ data, loading }) => {
  const { isEditMode } = useCardSize();

  if (loading || !data) {
    return (
      <Box sx={{ p: "16px 8px 16px" }}>
        <LinearProgress sx={{ mb: "4px" }} />
      </Box>
    );
  }

  const prefix = data[0].postfix;
  const fmtM = (v: number) => `${prefix}${(v / 1_000_000).toFixed(1)}M`;

  const deltas = data.map((q, i) => {
    const prevCalled = i > 0 ? data[i - 1].called : 0;
    const prevDistributed = i > 0 ? data[i - 1].distributed : 0;
    const callDelta = q.called - prevCalled;
    const distDelta = q.distributed - prevDistributed;
    return {
      quarter: q.quarter,
      calls: -callDelta,
      distributions: distDelta
    };
  });

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 280,
      margin: [16, 16, 80, 64],
      style: { fontFamily: "inherit" }
    },
    title: { text: undefined },
    credits: { enabled: false },
    exporting: { enabled: isEditMode },
    xAxis: {
      categories: deltas.map((d) => d.quarter),
      labels: {
        style: { fontSize: "11px", color: "#6B7280" },
        rotation: -35,
        align: "right"
      },
      lineColor: "#E5E7EB",
      tickColor: "transparent"
    },
    yAxis: {
      title: { text: null },
      labels: {
        formatter() {
          return fmtM(this.value as number);
        },
        style: { fontSize: "11px", color: "#6B7280" }
      },
      gridLineColor: "#F3F4F6",
      plotLines: [
        {
          value: 0,
          color: "#9CA3AF",
          width: 1,
          zIndex: 3
        }
      ]
    },
    tooltip: {
      shared: true,
      formatter() {
        return (
          this.points
            ?.map((p) => {
              const absVal = Math.abs(p.y ?? 0);
              return `${p.series.name}: <b>${fmtM(absVal)}</b>`;
            })
            .join("<br/>") ?? ""
        );
      }
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderWidth: 0,
        borderRadius: 2
      }
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      margin: 4,
      itemDistance: 16,
      itemStyle: {
        fontSize: "11px",
        fontWeight: "normal",
        color: "#374151"
      }
    },
    series: [
      {
        type: "column",
        name: "Capital Calls",
        data: deltas.map((d) => d.calls),
        color: "#C53030"
      },
      {
        type: "column",
        name: "Distributions",
        data: deltas.map((d) => d.distributions),
        color: "#071f35"
      }
    ]
  };

  return (
    <Box sx={{ p: "16px 8px 8px" }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#6B7280",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          pl: "12px",
          mb: "4px"
        }}
      >
        Cash Flow Timeline
      </Typography>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </Box>
  );
};